import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock, ClipboardList, Lock, Flame, ArrowRight, Bell, Gamepad2, Star,
} from 'lucide-react';
import { motion } from 'framer-motion';
import * as Sentry from '@sentry/react';
import NavShell from '../components/NavShell';
import { ElevatedPayoutExtras } from '../components/ElevatedPayoutExtras';
import SurveyStartModal from '../components/SurveyStartModal';
import CustomQuestionPrescreenModal from '../components/CustomQuestionPrescreenModal';
import WelcomeSurveyModal from '../components/WelcomeSurveyModal';
import WelcomeSurveyBanner from '../components/WelcomeSurveyBanner';
import { startManualSurvey, type ManualStartResponse } from '../lib/manualClick';
import type { RankedSurvey } from '@swipeopinion/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRankedSurveys } from '../hooks/useSurveys';
import { useMe } from '../hooks/useProfile';
import { fetchPrescreenQuestion, submitPrescreenProfileAnswer, fetchWelcomeSurvey } from '../lib/api';
import {
  scoreSurveys, diversityRerank, getTopPicks,
  getLoi, getMatchTier, matchTierPillClasses, type ScoredSurvey,
} from '../lib/surveyRanking';
import { useSurveyResultListener, type SurveyResultMessage } from '../hooks/useSurveyTabClose';

type FilterType = 'all' | 'quick' | 'high_value' | 'new' | 'high_match' | 'filling';
type SortType = 'recommended' | 'payout' | 'ppm' | 'shortest' | 'match';

export default function SurveysPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const { data: surveys = [], isLoading, error, refetch } = useRankedSurveys();
  const [selectedSurvey, setSelectedSurvey] = useState<RankedSurvey | null>(null);
  // Phase 2.5 — custom-question prescreen state for manual exchange.
  // Set by the click dispatcher when /api/manual/start returns
  // kind:'prescreen_required'. Cleared on submit (pass or fail).
  const [manualPrescreen, setManualPrescreen] = useState<
    | { sessionId: string; surveyId: string; customQuestion: import('../lib/manualClick').CustomQuestionPayload }
    | null
  >(null);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem('so_dismissed_surveys');
      return stored ? new Set(JSON.parse(stored) as string[]) : new Set();
    } catch { return new Set(); }
  });
  const [toast, setToast] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [sort, setSort] = useState<SortType>('recommended');

  // Prescreen profiling question
  type PQ = Awaited<ReturnType<typeof fetchPrescreenQuestion>>['question'];
  const [prescreenQ, setPrescreenQ] = useState<PQ>(null);

  function getAskedQuals(): number[] {
    try { return JSON.parse(sessionStorage.getItem('so_asked_quals') ?? '[]') as number[]; } catch { return []; }
  }
  function addAskedQual(qc: number) {
    const asked = getAskedQuals();
    if (!asked.includes(qc)) { asked.push(qc); sessionStorage.setItem('so_asked_quals', JSON.stringify(asked)); }
  }

  useEffect(() => {
    if (!surveys.length) return;
    const asked = getAskedQuals();
    if (asked.length >= 5) return;
    void fetchPrescreenQuestion(asked).then((r) => setPrescreenQ(r.question)).catch(() => {});
  }, [surveys.length]);

  function dismissSurvey(surveyId: string) {
    setHiddenIds((prev) => {
      const next = new Set(prev).add(surveyId);
      try { sessionStorage.setItem('so_dismissed_surveys', JSON.stringify([...next])); } catch { /* noop */ }
      return next;
    });
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  const handleSurveyResult = useCallback((msg: SurveyResultMessage) => {
    if (msg.type === 'complete') {
      showToast(`Survey complete! +${msg.points ?? 0} points credited.`);
    } else if (msg.type === 'screenout') {
      const pts = msg.consolation ?? 0;
      showToast(pts > 0 ? `That survey wasn't a match — +${pts} consolation points.` : "That survey wasn't a match — here's another one");
    } else if (msg.type === 'quotafull') {
      showToast("That survey just filled up — try another one");
    } else if (msg.type === 'security-terminate') {
      showToast('This survey ended early — no points earned.');
    }
    void refetch();
  }, [refetch]);

  useSurveyResultListener(handleSurveyResult);

  const { scored, topPicks, stats } = useMemo(() => {
    const visible = surveys.filter((s) => !hiddenIds.has(s.id));
    const allScored = scoreSurveys(visible);
    const ranked = diversityRerank(allScored);
    const picks = getTopPicks(allScored);
    const highMatchCount = allScored.filter((s) => s.matchIr >= 70).length;
    const bestCash = allScored.length > 0 ? Math.max(...allScored.map((s) => s.points_value)) / 100 : 0;
    return { scored: ranked, topPicks: picks, stats: { total: allScored.length, bestCash, highMatchCount } };
  }, [surveys, hiddenIds]);

  const topPickIds = useMemo(() => {
    const ids = new Set<string>();
    if (topPicks.bestMatch) ids.add(topPicks.bestMatch.id);
    if (topPicks.highestPaying) ids.add(topPicks.highestPaying.id);
    if (topPicks.fastestEarn) ids.add(topPicks.fastestEarn.id);
    return ids;
  }, [topPicks]);

  const filteredSurveys = useMemo(() => {
    let list = scored.filter((s) => !topPickIds.has(s.id));
    if (filter === 'quick') list = list.filter((s) => getLoi(s) < 10);
    if (filter === 'high_value') list = list.filter((s) => s.ptsPerMin >= 5);
    if (filter === 'new') list = list.filter((s) => s.isNew);
    if (filter === 'high_match') list = list.filter((s) => s.matchIr >= 70);
    if (filter === 'filling') list = list.filter((s) => s.isFillingFast);
    if (sort === 'payout') list = [...list].sort((a, b) => b.points_value - a.points_value);
    if (sort === 'ppm') list = [...list].sort((a, b) => b.ptsPerMin - a.ptsPerMin);
    if (sort === 'shortest') list = [...list].sort((a, b) => getLoi(a) - getLoi(b));
    if (sort === 'match') list = [...list].sort((a, b) => b.matchIr - a.matchIr);
    return list;
  }, [scored, topPickIds, filter, sort]);

  function handleDeny() {
    if (selectedSurvey) dismissSurvey(selectedSurvey.id);
    setSelectedSurvey(null);
    showToast("Got it — we'll find better matches for you");
    void refetch();
  }

  // Phase 2.5 — wall click dispatcher.
  //
  // Manual surveys go through /api/manual/start which decides
  // (server-side) whether the member proceeds to the buyer's URL,
  // needs a custom-question prescreen, or is screened out. Other
  // exchanges keep the existing setSelectedSurvey → SurveyStartModal
  // flow.
  async function handleSurveyClick(survey: RankedSurvey) {
    if (survey.exchange !== 'manual') {
      setSelectedSurvey(survey);
      return;
    }
    try {
      const result = await startManualSurvey(survey.id);
      dispatchManualStart(survey.id, result);
    } catch {
      showToast('Something went wrong opening that survey. Try again in a moment.');
    }
  }

  function dispatchManualStart(surveyId: string, result: ManualStartResponse) {
    if (result.kind === 'redirect') {
      window.location.href = result.url;
      return;
    }
    if (result.kind === 'prescreen_required') {
      const customQual = result.unknownQuals.find((q) => q.customQuestion);
      if (customQual?.customQuestion) {
        setManualPrescreen({
          sessionId: result.sessionId,
          surveyId,
          customQuestion: customQual.customQuestion,
        });
        return;
      }
      // Attribute-based unknownQuals from manual surveys would route
      // through the existing PrescreenModal flow once that integration
      // ships. Phase 2.5 only wires the custom-question case; the
      // soft toast keeps the click from being silently swallowed.
      showToast("This survey isn't ready for you just yet — try another.");
      return;
    }
    // screenout — admin-side gating excluded the member.
    dismissSurvey(surveyId);
    showToast("This survey isn't a fit right now.");
    void refetch();
  }

  async function handleManualPrescreenPass() {
    const surveyId = manualPrescreen?.surveyId;
    setManualPrescreen(null);
    if (!surveyId) return;
    try {
      const result = await startManualSurvey(surveyId);
      dispatchManualStart(surveyId, result);
    } catch {
      showToast('Something went wrong opening that survey.');
    }
  }

  function handleManualPrescreenFail() {
    const surveyId = manualPrescreen?.surveyId;
    setManualPrescreen(null);
    if (surveyId) dismissSurvey(surveyId);
    showToast("This survey isn't a fit right now.");
    void refetch();
  }

  function handleSurveyUnavailable(surveyId: string) {
    dismissSurvey(surveyId);
    showToast("That survey just filled up — try another one");
    void refetch();
  }

  const showContent = me?.onboarding_complete;

  // Guaranteed first win (T9) — pin a welcome survey for members who haven't
  // claimed it. Gated on the /me flag so the card vanishes once completed.
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeEligible = showContent === true && me?.welcome_survey_completed === false;
  const { data: welcome } = useQuery({
    queryKey: ['welcome-survey'],
    queryFn: fetchWelcomeSurvey,
    enabled: welcomeEligible,
    staleTime: 5 * 60 * 1000,
  });
  const welcomeAvailable = welcomeEligible && welcome?.available === true;

  return (
    <NavShell>
      <motion.main initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="px-4 sm:px-6 py-6 space-y-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1e1b4b] to-[#312e81] p-6 sm:p-8 text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="relative flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-display">Surveys</h1>
              <p className="text-purple-200 mt-1 text-sm">Pick the best match and start earning in minutes</p>
            </div>
            {showContent && !isLoading && stats.total > 0 && (
              <div className="flex gap-3 flex-wrap">
                <div className="bg-white/15 backdrop-blur-sm rounded-full px-4 py-2.5">
                  <span className="text-xl font-bold tabular-nums">{stats.total}</span>
                  <span className="text-purple-200 text-sm ml-1">Available</span>
                </div>
                <div className="bg-white/15 backdrop-blur-sm rounded-full px-4 py-2.5">
                  <span className="text-xl font-bold tabular-nums">${stats.bestCash.toFixed(2)}</span>
                  <span className="text-purple-200 text-sm ml-1">Top earn</span>
                </div>
                {stats.highMatchCount > 0 && (
                  <div className="bg-white/15 backdrop-blur-sm rounded-full px-4 py-2.5">
                    <span className="text-xl font-bold tabular-nums">{stats.highMatchCount}</span>
                    <span className="text-purple-200 text-sm ml-1">High match</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Onboarding gate */}
        {me && !me.onboarding_complete && (
          <div className="bg-[#EEF2FF] border border-[#5B4FE8]/20 rounded-2xl p-6 text-center space-y-4">
            <Lock className="w-10 h-10 text-[#5B4FE8] mx-auto" />
            <h2 className="text-lg font-bold text-[#111827]">Complete your profile to unlock surveys</h2>
            <p className="text-sm text-[#6B7280]">Step {(me.member as { onboarding_step?: number })?.onboarding_step ?? 0} of 4 complete</p>
            <Link to="/onboarding" className="inline-block py-3 px-6 bg-[#5B4FE8] text-white font-medium rounded-full hover:bg-[#4338CA] transition-colors">Continue Profile</Link>
          </div>
        )}

        {/* Welcome survey — guaranteed first win, pinned above the wall (T9).
            Shared component (B2) — also rendered on the dashboard. */}
        {welcomeAvailable && (
          <WelcomeSurveyBanner points={welcome?.points ?? 50} onStart={() => setShowWelcome(true)} />
        )}

        {/* Top Picks */}
        {showContent && !isLoading && stats.total >= 3 && (
          <>
            <p className="text-xs text-[#5B4FE8] font-medium flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-current" /> Start with these — highest success rate for your profile</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {([
                { pick: topPicks.bestMatch, label: 'Best Match', band: 'bg-[#5B4FE8]' },
                { pick: topPicks.highestPaying, label: 'Highest Paying', band: 'bg-[#10B981]' },
                { pick: topPicks.fastestEarn, label: 'Fastest Earn', band: 'bg-amber-500' },
              ] as const).map(({ pick, label, band }) => {
                if (!pick) return null;
                const loi = getLoi(pick);
                return (
                  <div key={`pick-${pick.id}`} onClick={() => { void handleSurveyClick(pick); }}
                    className="bg-white rounded-2xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all flex flex-col">
                    <div className={`${band} px-4 py-2 text-white text-xs font-bold`}>{label}</div>
                    <div className="p-4 flex flex-col flex-1">
                      <p className="text-sm font-bold text-[#111827] line-clamp-1">{pick.displayTitle}</p>
                      <div className="flex items-baseline gap-2 mt-1.5 flex-wrap">
                        <span className="text-xl font-bold text-[#111827] tabular-nums">${pick.cashValue}</span>
                        <ElevatedPayoutExtras survey={pick} />
                        <span className="text-xs text-[#9CA3AF]">{pick.points_value} pts</span>
                      </div>
                      <span className="flex items-center gap-1 mt-1 text-xs text-[#9CA3AF]">
                        <Clock className="w-3 h-3" /> {loi} min
                      </span>
                      <div className="mt-auto pt-3">
                        <span className="w-full inline-flex items-center justify-center gap-1 bg-[#5B4FE8] text-white text-xs font-semibold py-2 rounded-full">
                          Start <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Filter + Sort */}
        {showContent && !isLoading && stats.total > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {(['all', 'quick', 'high_value', 'new', 'high_match', 'filling'] as FilterType[]).map((key) => {
              const labels: Record<FilterType, string> = { all: 'All', quick: 'Quick', high_value: 'High Value', new: 'New', high_match: 'High Match', filling: 'Filling Fast' };
              return (
                <button key={key} onClick={() => setFilter(key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === key ? 'bg-[#5B4FE8] text-white' : 'bg-gray-100 text-[#6B7280] hover:bg-gray-200'}`}>
                  {labels[key]}
                </button>
              );
            })}
            <select value={sort} onChange={(e) => setSort(e.target.value as SortType)}
              className="ml-auto text-xs border border-[#E5E7EB] rounded-lg px-2 py-1.5 text-[#6B7280] bg-white">
              <option value="recommended">Recommended</option>
              <option value="payout">Highest Payout</option>
              <option value="ppm">Best $/min</option>
              <option value="shortest">Shortest</option>
              <option value="match">Best Match</option>
            </select>
          </div>
        )}

        {showContent && isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="bg-white rounded-2xl shadow-sm p-6 h-48 animate-pulse" />)}
          </div>
        )}
        {showContent && error && <div className="text-center py-16 text-[#EF4444]">Failed to load surveys. Please try again.</div>}
        {showContent && !isLoading && !error && filteredSurveys.length === 0 && (
          <div className="py-12 text-center space-y-5">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto" />
            <div>
              <p className="text-[#111827] font-semibold">No surveys match you right now</p>
              <p className="text-sm text-[#6B7280] mt-1">New ones arrive all day. Here's how to get ahead while you wait:</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto text-left">
              <Link to="/profile" className="group bg-white rounded-2xl shadow-sm p-4 hover:shadow-md transition-all">
                <div className="w-9 h-9 rounded-xl bg-[#EEF2FF] flex items-center justify-center mb-2">
                  <Bell className="w-4 h-4 text-[#5B4FE8]" />
                </div>
                <p className="text-sm font-semibold text-[#111827]">Notify me when surveys match</p>
                <p className="text-xs text-[#6B7280] mt-0.5">Turn on alerts so you're first in line.</p>
              </Link>
              <Link to={me?.onboarding_complete ? '/profile' : '/onboarding'} className="group bg-white rounded-2xl shadow-sm p-4 hover:shadow-md transition-all">
                <div className="w-9 h-9 rounded-xl bg-[#E6F7EE] flex items-center justify-center mb-2">
                  <ClipboardList className="w-4 h-4 text-[#10B981]" />
                </div>
                <p className="text-sm font-semibold text-[#111827]">Complete your profile</p>
                <p className="text-xs text-[#6B7280] mt-0.5">More detail unlocks more surveys.</p>
              </Link>
              <Link to="/games" className="group bg-white rounded-2xl shadow-sm p-4 hover:shadow-md transition-all">
                <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center mb-2">
                  <Gamepad2 className="w-4 h-4 text-amber-600" />
                </div>
                <p className="text-sm font-semibold text-[#111827]">Play games to earn</p>
                <p className="text-xs text-[#6B7280] mt-0.5">Pick up points while you wait.</p>
              </Link>
            </div>
          </div>
        )}

        {/* Survey grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSurveys.map((survey, idx) => {
            const loi = getLoi(survey);
            const isFeatured = survey.points_value >= 60;
            const isTier1 = idx < 3;
            const tier = getMatchTier(survey.matchIr, survey.exchange);

            return (
              <div key={survey.id} onClick={() => { void handleSurveyClick(survey); }}
                className={`group bg-white rounded-2xl shadow-sm p-5 flex flex-col cursor-pointer hover:shadow-lg transition-all duration-300 ${
                  isFeatured ? 'border-l-4 border-l-[#5B4FE8]' : ''
                } ${isTier1 ? 'border-[1.5px] border-[#C7C2F8] bg-[#FAFAFE]' : ''}`}>

                {/* Top: match label (left) + time (right) — three honest signals */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${matchTierPillClasses(tier.tone)}`}>
                    {tier.text}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-[#9CA3AF]">
                    <Clock className="w-3.5 h-3.5" /> {loi} min
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-base font-bold text-[#111827] leading-snug mb-2 line-clamp-2 group-hover:text-[#5B4FE8] transition-colors">
                  {survey.displayTitle}
                </h3>

                {/* Reward — the visual anchor. L3-L5 members see their elevated
                    payout with the base rate struck through + a trust-bonus pill;
                    L1-L2 (no bonus) see the single number, unchanged. */}
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-2xl font-bold text-[#111827] tabular-nums tracking-tight">${survey.cashValue}</span>
                  <ElevatedPayoutExtras survey={survey} />
                  <span className="text-xs text-[#9CA3AF]">{survey.points_value} pts</span>
                </div>

                {/* Filling-fast tag (no fabricated bar) — only when the live
                    open-minute count is genuinely low */}
                {survey.isFillingFast && (
                  <p className="text-[11px] text-red-600 font-medium flex items-center gap-1 mt-2">
                    <Flame className="w-3 h-3" /> Filling fast
                  </p>
                )}

                {/* CTA — pinned to bottom; the whole card is the tap target */}
                <div className="mt-auto pt-4">
                  <span className="w-full inline-flex items-center justify-center gap-1 bg-[#5B4FE8] text-white text-sm font-semibold py-2.5 rounded-full group-hover:bg-[#4338CA] transition-colors">
                    Start <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </motion.main>

      {toast && (
        <div className="fixed bottom-24 sm:bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#111827] text-white text-sm font-medium px-5 py-3 rounded-xl shadow-md">{toast}</div>
      )}

      {selectedSurvey && (
        <SurveyStartModal
          key={selectedSurvey.id}
          survey={selectedSurvey}
          onClose={() => setSelectedSurvey(null)}
          onDeny={handleDeny}
          onSurveyUnavailable={handleSurveyUnavailable}
          onStarted={(id) => {
            // Instant visual removal (local hiddenIds) + bust the shared
            // ['surveys'] cache so the wall is fresh on return. Covers both
            // this page and the Dashboard since they share the query key.
            dismissSurvey(id);
            void queryClient.invalidateQueries({ queryKey: ['surveys'] });
          }}
          onDisqualified={(id) => dismissSurvey(id)}
          onTryNext={(next) => setSelectedSurvey(next)}
          prescreenQuestion={prescreenQ}
          onPrescreenAnswer={(qc, codes, val, attrKey) => {
            addAskedQual(qc);
            void submitPrescreenProfileAnswer({ qualCode: qc, conditionCodes: codes, attributeValue: val, attributeKey: attrKey }).catch((err) => {
              // Best-effort profile enrichment — report it and let the member
              // know it didn't stick rather than failing silently.
              Sentry.captureException(err);
              showToast("Couldn't save that answer — we'll ask again later.");
            });
            setPrescreenQ(null);
            // Preload next question in background
            void fetchPrescreenQuestion(getAskedQuals()).then((r) => setPrescreenQ(r.question)).catch(() => {});
          }}
          onPrescreenSkip={(qc) => {
            addAskedQual(qc);
            setPrescreenQ(null);
            void fetchPrescreenQuestion(getAskedQuals()).then((r) => setPrescreenQ(r.question)).catch(() => {});
          }}
        />
      )}

      {manualPrescreen && (
        <CustomQuestionPrescreenModal
          sessionId={manualPrescreen.sessionId}
          questionHash={manualPrescreen.customQuestion.questionHash}
          question={manualPrescreen.customQuestion.question}
          type={manualPrescreen.customQuestion.type}
          options={manualPrescreen.customQuestion.options}
          required={manualPrescreen.customQuestion.required}
          onPass={() => { void handleManualPrescreenPass(); }}
          onFail={handleManualPrescreenFail}
          onClose={() => setManualPrescreen(null)}
        />
      )}

      {showWelcome && welcome && (
        <WelcomeSurveyModal
          questions={welcome.questions}
          points={welcome.points}
          onClose={() => setShowWelcome(false)}
          onTakeSurvey={() => { setShowWelcome(false); void refetch(); }}
        />
      )}
    </NavShell>
  );
}
